import React from 'react';
import { Pressable, StyleProp, View, ViewStyle } from 'react-native';
import { RADIUS, shadow } from '../../assets/theme';
import { makeStyles, useAppTheme } from '../../theme/ThemeContext';

type CardProps = {
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  onPress?: () => void;
  /** Remove the inner padding (e.g. for lists with their own row padding). */
  flush?: boolean;
  /** Lift the card with a stronger shadow (hero / highlighted content). */
  raised?: boolean;
  accessibilityLabel?: string;
};

/**
 * Rounded surface with a hairline border and soft shadow. Pressable cards lift on hover (web).
 */
export default function Card({ children, style, onPress, flush, raised, accessibilityLabel }: CardProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const base = [styles.card, shadow(colors, raised ? 2 : 1), flush ? null : styles.padded];

  if (!onPress) {
    return <View style={[base, style]}>{children}</View>;
  }

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={(state) => {
        const { pressed } = state;
        const hovered = (state as { hovered?: boolean }).hovered;
        return [
          base,
          hovered ? [shadow(colors, 2), styles.hovered] : null,
          pressed ? styles.pressed : null,
          style,
        ];
      }}
    >
      {children}
    </Pressable>
  );
}

const useStyles = makeStyles((c) => ({
  card: {
    backgroundColor: c.surface,
    borderRadius: RADIUS.lg,
    borderWidth: 1,
    borderColor: c.divider,
  },
  padded: { padding: 20 },
  hovered: { borderColor: c.border },
  pressed: { transform: [{ scale: 0.99 }] },
}));
